<template>
  <div
      v-if="position && content !== ''"
      ref="labelContainer"
      class="absolute w-[50%] h-[50%] pointer-events-none"
      :class="[
            'tl' === position && 'top-0 left-0',
            'tr' === position && 'top-0 right-0',
            'br' === position && 'bottom-0 right-0',
            'bl' === position && 'bottom-0 left-0',
        ]"
  >
    <div
        class="h-full absolute"
        :class="[
                'tl' === position && 'top-0 left-0',
                'tr' === position && 'top-0 right-0',
                'br' === position && 'bottom-0 right-0',
                'bl' === position && 'bottom-0 left-0',
            ]"
        :style="{ height: size + '%' }"
    >
      <div
          class="absolute shadow "
          :class="[
                    ['tr', 'br'].includes(position) && '-right-[100%]',
                    ['bl', 'br'].includes(position) && 'bottom-[-60%]',
                    ['tl', 'tr'].includes(position) && 'top-[-60%]',
                ]"
          :style="{ height: 100 + '%' }"
      >
        <div
            class="w-2 h-full absolute "
            :class="[
                        ['tl', 'bl'].includes(position) && '-left-[4.5px]',
                        ['tr', 'br'].includes(position) && '-right-[4.5px] -scale-x-100',
                    ]"
            :style="{
                        clipPath:
                            ' polygon( 5.412% 15.206%, 5.412% 15.206%, 2.52% 13.361%,0.8% 11.382%,0.177% 9.346%,0.575% 7.331%,1.921% 5.413%,4.137% 3.671%,7.15% 2.181%,10.883% 1.021%,15.262% 0.268%,20.211% 0%,120% 0%,120% 100%,20.211% 100%,20.211% 100%,15.262% 99.732%,10.883% 98.979%,7.15% 97.819%,4.137% 96.329%,1.921% 94.587%,0.575% 92.669%,0.177% 90.654%,0.8% 88.618%,2.52% 86.639%,5.412% 84.794%,75% 50%,5.412% 15.206% )',
                        backgroundColor: background,
                    }"
        />
        <div
            ref="textContainerContent"
            class="h-[100%] px-3 flex items-center font-semibold whitespace-nowrap"
            :class="[
                        ['tl', 'bl'].includes(position) && 'rounded-r-sm',
                        ['tr', 'br'].includes(position) && 'rounded-l-sm',
                    ]"
            :style="{ backgroundColor: background, color: text }"
        >
          {{ content }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";

export default {
  props: {
    position:   {
      // tl,tr,bl,br
      type:    String,
      default: "tl",
    },
    background: {
      //bg color
      type:    String,
      default: "#ffffff",
    },
    text:       {
      //text color
      type:    String,
      default: "#eeeeee",
    },
    size:       {
      //width, height in % of container width
      type:    Number,
      default: "24",
    },
    content:    {
      //text inside label
      type:    String,
      default: "",
    },
  },

  setup(props) {
    const CONSOLE_MESSAGES = {
      MISSING_CONTAINERS:
          "Cannot initialize label - missing label container or text container!",
    };
    const labelContainer = ref(null);
    const textContainerContent = ref(null);
    const fontFamily = ref(null);
    const defaultFontSize = ref(12);

    const {content} = props;

    const setLabel = async () => {
      try {
        await nextTick();
        adjustFontSize();
      } catch (error) {
        console.error(
            "An unexpected error occurred in setLabel:",
            error
        );
      }
    };

    const adjustFontSize = () => {
      // make sure text fits inside container
      try {

        const lc = labelContainer.value?.getBoundingClientRect()
        const tc = textContainerContent.value;
        const ff = fontFamily.value;
        const padding = 12;
        let dff = defaultFontSize.value;

        if (!lc || !tc) {
          console.error(CONSOLE_MESSAGES.MISSING_CONTAINERS);
          return;
        }
        // Calculate the available space in the text container
        const availableSpace = lc.width;

        // Calculate the width of the text content
        const textContentWidth =
            measureTextWidth(content, dff, ff) + padding;

        // Calculate the initial font size based on the available space
        const initialFontSize =
            (availableSpace / textContentWidth) * dff;

        // Check if the text content overflows the container
        if (textContentWidth > availableSpace) {
          // Adjust the font size dynamically
          const adjustedFontSize =
              (availableSpace / textContentWidth) * initialFontSize;
          tc.style.fontSize = `${adjustedFontSize}px`;
        } else {
          // Reset the font size if no overflow
          tc.style.fontSize = dff + "px";
        }
      } catch (error) {
        console.error(
            "An unexpected error occurred in adjustFontSize:",
            error
        );
      }
    };

    const measureTextWidth = (text, fontSize, fontFamily) => {
      // Create a temporary span element
      const tempSpan = document.createElement("span");

      // Set the font size and family
      tempSpan.style.fontSize = `${fontSize}px`;
      tempSpan.style.fontFamily = fontFamily;

      // Set the text content
      tempSpan.textContent = text;

      // Append the span to the document (not visible)
      document.body.appendChild(tempSpan);

      // Measure the width of the text
      const width = tempSpan.offsetWidth;

      // Remove the temporary span from the document
      document.body.removeChild(tempSpan);

      return width;
    };

    const extractFontFamily = () => {
      const element = textContainerContent.value;

      if (element) {
        const computedStyle = window.getComputedStyle(element);
        const fontFamily = computedStyle.fontFamily;

        // Split the font family string into an array
        const fontFamiliesArray = fontFamily.split(",");

        // Trim each font family to remove leading/trailing spaces
        return fontFamiliesArray.map((font) =>
            font.trim()
        );
      } else {
        // Return an empty array if the element is not found
        return [];
      }
    };

    const extractFontSize = () => {
      //extract default font of label if not present retain default
      const element = textContainerContent.value;

      if (!element) return defaultFontSize.value;

      const computedStyle = window.getComputedStyle(element);
      return parseInt(computedStyle.fontSize, 10);
    };

    onMounted(() => {
      defaultFontSize.value = extractFontSize()

      //get font family to be able measure text width
      const fontFamilies = extractFontFamily()

      if (fontFamilies && fontFamilies.length) {
        fontFamily.value = fontFamilies[0];
      }

      setLabel();

    });

    return {
      labelContainer,
      textContainerContent,
      content,
      fontFamily,
    };
  },
};
</script>
